import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { AIAgentRule, getAgentRules } from '../../lib/aiAgents';
import AgentRuleCard from './AgentRuleCard';

interface AgentRulesPanelProps {
  agentId: string;
  refreshTrigger: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  pricing: 'Pricing',
  surcharges: 'Surcharges',
  conditions: 'Conditions',
  items: 'Items',
  discounts: 'Discounts',
  general: 'General'
};

const CATEGORY_ORDER = ['pricing', 'surcharges', 'items', 'conditions', 'discounts', 'general'];

export default function AgentRulesPanel({ agentId, refreshTrigger }: AgentRulesPanelProps) {
  const [rules, setRules] = useState<AIAgentRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [newRuleIds, setNewRuleIds] = useState<Set<string>>(new Set());
  const prevRuleIdsRef = useRef<Set<string>>(new Set());

  const loadRules = async () => {
    try {
      const data = await getAgentRules(agentId);
      
      const currentIds = new Set(data.map(r => r.id));
      const prevIds = prevRuleIdsRef.current;
      const freshIds = new Set<string>();
      currentIds.forEach(id => {
        if (!prevIds.has(id)) freshIds.add(id);
      });
      prevRuleIdsRef.current = currentIds;

      if (freshIds.size > 0) {
        setNewRuleIds(freshIds);
        setTimeout(() => setNewRuleIds(new Set()), 3000);
      }

      setRules(data);
    } catch (err) {
      console.error('Failed to load rules:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, [agentId, refreshTrigger]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const grouped: Record<string, AIAgentRule[]> = {};
  for (const rule of rules) {
    const cat = rule.category || 'general';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(rule);
  }

  const sortedCategories = CATEGORY_ORDER.filter(cat => grouped[cat]?.length > 0);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">What I've Learned</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
            {rules.filter(r => r.is_active).length}
          </span>
        </div>
        <button
          onClick={loadRules}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh rules"
        >
          <RefreshCw className="h-3.5 w-3.5 text-gray-400" />
        </button>
      </div>

      {/* Rules List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-8">
            <BookOpen className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No rules yet</p>
            <p className="text-xs text-gray-400 mt-1">Start chatting to teach your agent</p>
          </div>
        ) : (
          sortedCategories.map(category => (
            <div key={category}>
              <button
                onClick={() => toggleCategory(category)}
                className="flex items-center gap-1.5 mb-1.5 w-full text-left"
              >
                {collapsedCategories.has(category) ? (
                  <ChevronRight className="h-3 w-3 text-gray-400" />
                ) : (
                  <ChevronDown className="h-3 w-3 text-gray-400" />
                )}
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {CATEGORY_LABELS[category] || category}
                </span>
                <span className="text-xs text-gray-400">({grouped[category].length})</span>
              </button>
              {!collapsedCategories.has(category) && (
                <div className="space-y-1.5 ml-1">
                  {grouped[category].map(rule => (
                    <AgentRuleCard
                      key={rule.id}
                      rule={rule}
                      isNew={newRuleIds.has(rule.id)}
                      onUpdated={loadRules}
                      onDeleted={loadRules}
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
